import getLang from 'LearningCenterUI/utils/getLang';
import { useCallback, useMemo } from 'react';
import sortBy from 'hs-lodash/sortBy';
import { useFullLesson } from '../lessons';
import http from 'hub-http/clients/apiClient';
import { BASE_URI_PROGRESS } from '../../constants/ApiConstants';
import { registerMutation, useMutation } from 'academy-common-lib/utils/DataFetchingClient';
import { addLessonQuizAttemptAnswerCache, clearLessonQuizAttemptCache, deleteContentProgressFromCache, removeLessonQuizAttemptAnswerCache, useSetContentProgressStatusCache, setLessonQuizAttemptCache, useLessonProgressById, addLessonStartQuizAttemptAnswerCache } from '../userProgress';
import { getPlaylistIdFromUrl } from '../../utils/UrlParamUtils';
const BASE_QUIZ_ATTEMPTS_URI = `${BASE_URI_PROGRESS}/quiz-attempts`;
const START_QUIZ_ATTEMPT = registerMutation({
  fieldName: 'startQuiz',
  args: ['contentId', 'language'],
  fetcher({
    contentId,
    language = getLang()
  }) {
    return http.post(`${BASE_QUIZ_ATTEMPTS_URI}/start`, {
      data: {
        contentId,
        language
      }
    });
  }
});
const SUBMIT_QUIZ_ANSWER = registerMutation({
  fieldName: 'answerQuiz',
  args: ['attemptId', 'questionId', 'answerIds'],
  fetcher({
    attemptId,
    questionId,
    answerIds
  }) {
    return http.post(`${BASE_QUIZ_ATTEMPTS_URI}/${attemptId}/answer`, {
      data: {
        questionId,
        answerIds
      }
    });
  }
});
const FINISH_QUIZ_ATTEMPT = registerMutation({
  fieldName: 'finishQuiz',
  args: ['attemptId', 'trackId', 'lessonId', 'playlistId'],
  fetcher({
    attemptId,
    language,
    lessonId,
    playlistId,
    trackId
  }) {
    return http.post(`${BASE_QUIZ_ATTEMPTS_URI}/${attemptId}/progress/finish`, {
      data: {
        language,
        lessonId,
        playlistId,
        trackId
      }
    });
  }
});
export const useStartQuiz = () => {
  const [mutate, {
    loading: startQuizLoading,
    data: startQuizData,
    error: startQuizError,
    called: startQuizCalled
  }] = useMutation(START_QUIZ_ATTEMPT);
  const startQuiz = useCallback((quizId, language) => {
    return mutate({
      variables: {
        contentId: quizId,
        language
      }
    });
  }, [mutate]);
  return [startQuiz, {
    startQuizLoading,
    startQuizData,
    startQuizError,
    startQuizCalled
  }];
};
export const useSubmitAnswer = () => {
  const [mutate, {
    loading: submitQuizAnswerLoading,
    data: submitQuizAnswerData,
    error: submitQuizAnswerError,
    called: submitQuizAnswerCalled
  }] = useMutation(SUBMIT_QUIZ_ANSWER);
  const submitQuizAnswer = useCallback((lessonId, attemptId, questionId, answerIds, startQuizInfo) => {
    return mutate({
      variables: {
        attemptId,
        questionId,
        answerIds
      },
      update(__, attempt) {
        if (attempt.data) {
          if (startQuizInfo) {
            addLessonStartQuizAttemptAnswerCache(lessonId, attemptId, questionId, answerIds, attempt.data.answerQuiz.correctAnswer, startQuizInfo);
          } else {
            addLessonQuizAttemptAnswerCache(lessonId, attemptId, questionId, answerIds, attempt.data.answerQuiz.correctAnswer);
          }
        }
      }
    });
  }, [mutate]);
  return [submitQuizAnswer, {
    submitQuizAnswerLoading,
    submitQuizAnswerData,
    submitQuizAnswerError,
    submitQuizAnswerCalled
  }];
};
export const useFinishQuiz = (quizLanguage = getLang()) => {
  const [mutate, {
    loading: finishQuizLoading,
    data: finishQuizData,
    error: finishQuizError,
    called: finishQuizCalled
  }] = useMutation(FINISH_QUIZ_ATTEMPT);
  const {
    setContentProgressStatusCache
  } = useSetContentProgressStatusCache({
    language: quizLanguage
  });
  const finishQuiz = useCallback((lessonId, quizId, attemptId, trackId, language = getLang()) => {
    const playlistId = getPlaylistIdFromUrl();
    return mutate({
      variables: {
        attemptId,
        language,
        lessonId,
        playlistId,
        trackId
      },
      update(__, attempt) {
        if (attempt.data) {
          setLessonQuizAttemptCache(lessonId, quizId, attempt.data.finishQuiz);
          setContentProgressStatusCache(quizId, 'COMPLETED', language);
        }
      }
    });
  }, [mutate, setContentProgressStatusCache]);
  return [finishQuiz, {
    finishQuizLoading,
    finishQuizData,
    finishQuizError,
    finishQuizCalled
  }];
};
export const useClearQuizAttempt = () => {
  return useCallback((lessonId, quizId, attemptId, contentLanguage) => {
    clearLessonQuizAttemptCache(lessonId, quizId, attemptId);
    deleteContentProgressFromCache(quizId, contentLanguage);
  }, []);
};
export const useClearQuizAnswer = () => {
  return useCallback(removeLessonQuizAttemptAnswerCache, []);
};
export const useQuiz = (lessonId, quizId, language = getLang()) => {
  const {
    lesson,
    lessonLoading,
    lessonError
  } = useFullLesson(lessonId, language);
  const quiz = useMemo(() => {
    const foundQuiz = lesson && lesson.content.find(content => content.id === quizId);
    if (!foundQuiz) return null;
    return Object.assign({}, foundQuiz, {
      // sort questions and answers by displayOrder
      questions: sortBy(foundQuiz.questions || [], q => q.displayOrder).map(q => Object.assign({}, q, {
        answers: sortBy(q.answers, a => a.displayOrder)
      }))
    });
  }, [lesson, quizId]);
  return {
    quiz,
    quizLoading: lessonLoading,
    quizError: lessonError
  };
};
export const useRecentQuizAttempt = (lessonId, quizId) => {
  const {
    lessonProgressById,
    lessonProgressByIdLoading,
    lessonProgressByIdError
  } = useLessonProgressById(lessonId);
  const recentQuizAttemptId = lessonProgressById && lessonProgressById.quizIdToRecentAttemptId[quizId];
  return {
    recentQuizAttempt: recentQuizAttemptId ? lessonProgressById.quizAttempts[recentQuizAttemptId] : null,
    recentQuizAttemptLoading: lessonProgressByIdLoading,
    recentQuizAttemptError: lessonProgressByIdError
  };
};